import { Box, styled, Button, Typography, Avatar } from '@mui/material';

export const MessageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  '&:last-child .timeline:after': { height: '100%' },
  [theme.breakpoints.down('md')]: {
    minWidth: 0,
    gap: theme.spacing(1),
    '& > :not([data-history-timeline])': { flex: '1 1 0', minWidth: 0, maxWidth: 'calc(100% - 28px)', margin: 0 },
    '& .MuiBox-root, & .MuiStack-root': { minWidth: 0, maxWidth: '100%' },
    '& .MuiTypography-root': { overflowWrap: 'anywhere' },
    '& .history-author, & .history-author .MuiBox-root': { flexWrap: 'wrap' },
    '& .history-email-header': { flexDirection: 'column', gap: theme.spacing(1) },
    '& audio, & video': { maxWidth: '100%' },
    '& .MuiIconButton-root, & .MuiButton-root': { minHeight: 44 },
  },
}));

export const MessageStack = styled(Box)({
  maxWidth: '100%',
  minWidth: 100,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
});

export const EditorContainer = styled(Box)(({ theme }) => ({
  ...theme.typography.body2,
  [theme.breakpoints.down('md')]: {
    minWidth: 0, maxWidth: '100%', overflowX: 'auto', overflowWrap: 'anywhere',
    pre: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' },
  },
  figure: {
    margin: 0,
  },
  b: {
    whiteSpace: 'break-spaces',
  },
  blockquote: {
    padding: theme.spacing(1),
    margin: 0,
    borderLeft: `2px solid ${theme.palette.primary.light}`,
  },
  p: {
    margin: 0,
    '&, > i': {
      wordWrap: 'break-word',
    },
  },
  img: {
    cursor: 'pointer',
    objectFit: 'cover',
    maxWidth: '100%',
    height: '100%',
    [theme.breakpoints.down('md')]: { height: 'auto' },
    borderRadius: theme.spacing(0.5),
  },
}));

export const ContentStack = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.contrastText,
  borderRadius: theme.spacing(1),
  gap: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
}));

export const ThreadToggleButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
  textTransform: 'none',
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingTop: theme.spacing(0.75),
  paddingBottom: theme.spacing(0.75),
  minHeight: 36,
  border: '1px solid',
  borderColor: theme.palette.alphas.divider,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.alphas.piedrazo,
    borderColor: theme.palette.primary.light,
    color: theme.palette.text.primary,
  },
  '&:active': {
    backgroundColor: theme.palette.alphas.cascote,
    borderColor: theme.palette.primary.main,
  },
  '&:disabled': {
    backgroundColor: theme.palette.paper.light,
    borderColor: theme.palette.disabled.light,
    color: theme.palette.disabled.main,
  },
  '& .MuiButton-startIcon': {
    marginRight: theme.spacing(0.75),
    color: 'inherit',
  },
}));

export const ThreadContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[300]}`,
  boxShadow: theme.shadows[1],
}));

export const ThreadHeader = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

export const ThreadEmailSeparator = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2.5),
  height: 12,
  borderLeft: `2px solid ${theme.palette.grey[600]}`,
  opacity: 0.4,
}));

export const ThreadEmailItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  position: 'relative',
  border: '1px solid transparent',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.grey[300],
    boxShadow: theme.shadows[1],
  },
}));

export const ThreadAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  fontSize: '0.875rem',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
}));

export const ThreadEmailContent = styled(Box)(() => ({
  wordBreak: 'break-all',

  '& p': {
    margin: '4px 0',
    fontSize: '0.875rem',
    lineHeight: 1.6,
  },
  '& p:first-of-type': {
    marginTop: 0,
  },
  '& p:last-of-type': {
    marginBottom: 0,
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 4,
  },
}));

export const ThreadDateBadge = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  borderRadius: theme.spacing(1),
  fontWeight: 500,
}));
